import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

import { useAuth } from '../lib/contexts/AuthContext';

const getWordPressBaseUrl = () => {
  const rawBaseUrl =
    process.env.REACT_APP_WOOCOMMERCE_API_URL ||
    process.env.REACT_APP_API_URL ||
    window.location.origin;

  return rawBaseUrl.replace(/\/$/, '');
};

const getBackendBaseUrl = () =>
  (process.env.REACT_APP_API_URL || window.location.origin).replace(/\/$/, '');

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const [error, setError] = useState<string>('');
  const toast = useToast();
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();

  useEffect(() => {if (isAuthenticated) navigate('/');}, [isAuthenticated, navigate]);

  useEffect(() => {                                                
    document.body.style.backgroundImage = "url('https://benbruno.com/wp-content/uploads/2022/12/heroimage_contrast-1.jpg')";
    document.body.style.backgroundSize = "cover";
    document.body.style.backgroundPosition = "center";
    document.body.style.backgroundRepeat = "no-repeat";            
    document.body.style.minHeight = "100vh";
                                                                   
    return () => {
      document.body.style.backgroundImage = "";  
      document.body.style.backgroundSize = "";
      document.body.style.backgroundPosition = "";                 
      document.body.style.backgroundRepeat = "";
      document.body.style.minHeight = "";                          
    };            
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const showLoginError = (message: string) => {
    setError(message);
    toast({
      title: 'Login failed',
      description: message,
      status: 'error',
      duration: 5000,
      isClosable: true,
    });
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    const authBaseUrl = getWordPressBaseUrl();
    const authEndpoint = `${authBaseUrl}/wp-json/jwt-auth/v1/token`;

    let response;
    try {
      response = await axios.post(
        authEndpoint,
        {
          username: formData.email,
          password: formData.password,
        }
      );
    } catch (err: any) {
      const isCredentialError = [400, 401, 403].includes(err?.response?.status);
      showLoginError(
        isCredentialError
          ? 'WordPress login failed. Please check your username and password.'
          : 'Unable to connect to WordPress. Please try again.'
      );
      return;
    }

    const token = response.data.token;
    try {
      const cmsAuthResponse = await axios.get(
        `${getBackendBaseUrl()}/api/users/cms_auth`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (Number(cmsAuthResponse.data?.role) !== 1) {
        showLoginError('CMS access denied. Admin account required.');
        return;
      }

      localStorage.setItem('strong_auth_token', token);
      login({
        token: response.data.token,
        user: cmsAuthResponse.data,
      });

      toast({
        title: 'Login successful!',
        description: 'You are now logged in.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      navigate('/');
    } catch (err: any) {
      const message =
        err?.response?.status === 403
          ? 'CMS access denied. Admin account required.'
          : 'Unable to verify CMS access. Please try again.';
      showLoginError(message);
    }
  };

  return (
    <Box
      maxW='400px'
      mx='auto'
      mt='200px'
      p='8'
      borderRadius='lg'
      boxShadow='lg'
      bg='white'
    >
      <VStack spacing={4} as='form' onSubmit={handleLogin}>
        <FormControl id='email' isRequired>
          <FormLabel>Username</FormLabel>
          <Input
            name='email'
            value={formData.email}
            onChange={handleInputChange}
            placeholder='Enter your username'
          />
        </FormControl>

        <FormControl id='password' isRequired>
          <FormLabel>Password</FormLabel>
          <Input
            type='password'
            name='password'
            value={formData.password}
            onChange={handleInputChange}
            placeholder='Enter your password'
          />
        </FormControl>

        {error && (
          <Box color='red.500' fontSize='sm'>
            {error}
          </Box>
        )}

        <Button colorScheme='primary' width='full' type='submit'>
          Login
        </Button>
      </VStack>
    </Box>
  );
};

export default Login;
